import { cartApi, updateCart } from '../../helpers';

const selectors = {
  quickAddButton: '[data-quick-add-button]',
  swatchLabel: '[data-swatch-label]',
  swatchInput: '[data-swatch-input]',
  productImage: '[data-product-image]',
  productPrice: '[data-product-price]',
};

/**
 * A single product item in a collection / list of products
 *
 * @export
 * @class ProductItem
 */
export default class ProductItem {
  /**
   * Creates an instance of ProductItem.
   * @param {HTMLElement} el
   */
  constructor(el) {
    this.el = el;

    this._initSwatches();
    this._initQuickAdd();
  }

  /**
   * Initialize swatch functionality
   */
  _initSwatches() {
    const swatchLabels = this.el.querySelectorAll(selectors.swatchLabel);

    if (!swatchLabels.length) {
      return;
    }

    swatchLabels.forEach(label => {
      label.addEventListener('click', this._handleSwatchChange.bind(this));
    });
  }

  /**
   * Handle swatch change events
   */
  _handleSwatchChange(event) {
    event.preventDefault();

    const label = event.target.closest(selectors.swatchLabel);
    const input = document.getElementById(label?.getAttribute('for'));

    if (!input) {
      return;
    }

    /** Updated all checked swatches to unchecked, then set the new checked swatch */
    this.el
      .querySelectorAll(`input[name="${input.name}"]`)
      .forEach(i => (i.checked = false));
    input.checked = true;

    /** Update image, price and quick add button state */
    this._updateImage(input);
    this._updatePrice(input);
    this._updateButtonState(input);
  }

  /**
   * Update product image
   */
  _updateImage(input) {
    const variantImage = input.getAttribute('data-variant-image');
    const productImage = this.el.querySelector(selectors.productImage);

    if (variantImage && productImage) {
      productImage.src = variantImage;
      productImage.srcset = '';
    }
  }

  /**
   * Update product price
   */
  _updatePrice(input) {
    const variantPrice = input.getAttribute('data-variant-price');
    const priceElement = this.el.querySelector(selectors.productPrice);

    if (!variantPrice || !priceElement) {
      return;
    }
    priceElement.textContent = '$' + (parseInt(variantPrice) / 100).toFixed(2);
  }

  /**
   * Update add to cart button state based on quantity
   */
  _updateButtonState(input = null) {
    const quickAddButton = this.el.querySelector(selectors.quickAddButton);
    if (!quickAddButton) {
      return;
    }

    /** Get quantity from input, checked swatch, or default variant */
    const variantQuantity = parseInt(
      input?.getAttribute('data-variant-quantity') ||
        this.el
          .querySelector(`${selectors.swatchInput}:checked`)
          ?.getAttribute('data-variant-quantity') ||
        this.el.dataset.defaultVariantQuantity ||
        '0',
    );

    quickAddButton.disabled = variantQuantity < 1;
  }

  /**
   * Initialize quick add functionality
   */
  _initQuickAdd() {
    const quickAddButton = this.el.querySelector(selectors.quickAddButton);

    if (!quickAddButton) {
      return;
    }

    quickAddButton.addEventListener('click', this._handleQuickAdd.bind(this));
  }

  /**
   * Handle quick add button click
   */
  _handleQuickAdd(event) {
    event.preventDefault();

    const checkedInput = this.el.querySelector(
      `${selectors.swatchInput}:checked`,
    );
    const variantId =
      checkedInput?.getAttribute('data-variant-id') ||
      this.el.dataset.defaultVariantId;

    if (variantId) {
      this._addToCart(variantId);
    }
  }

  /**
   * Add variant to cart
   */
  _addToCart(variantId) {
    const quickAddButton = this.el.querySelector(selectors.quickAddButton);
    if (!quickAddButton) {
      return;
    }

    /** Disable quick add button to prevent multiple clicks */
    quickAddButton.disabled = true;

    cartApi
      .add({ items: [{ id: variantId, quantity: 1 }] })
      .then(response => {
        quickAddButton.disabled = false;

        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        quickAddButton.disabled = false;
      });
  }
}
